import app from '../../../authentication/firebaseStart';
import { ROLES } from '../../../authentication/rbac';
import { DEVICE_STATUS } from '../../Dashboard/constant/Dashboard.constant';
import { DEFAULT_ORDER_BY, DEFAUTL_ORDER_DIRECTION, DEVICE_TYPE_STATUS, DEVICE_TYPE_VALUE } from './DeviceReports.constant';

const firestore = app.firestore();

// Helper function to execute queries and get total count
export const getTotalCount = async (context, { searchText, searchField, orderDirection, deviceType, orderBy }) => {
  try {
      // For queries, get the total count of matching documents
      const totalQuery = createQuery({
        context,
        deviceType,
        searchText,
        searchField,
        orderBy: searchField || DEFAULT_ORDER_BY,
        orderDirection: orderDirection || DEFAUTL_ORDER_DIRECTION
      });
      // This is needed for pagination to work correctly when going to last page. As we are using old version of firebase we don't have count() method to directly get the count for the query
      const countSnapshot = await totalQuery.get();
      return countSnapshot.size;
  } catch (error) {
    console.error('Error getting total count:', error);
    throw error;
  }
};

const getDeviceStatus = (device) => {
  const { missing, lost, decommission, recommission } = DEVICE_STATUS;
  const { statusMissing, statusLost, statusDcmd, statusRcmd } = DEVICE_TYPE_STATUS;
  if (device?.deviceState === recommission) {
    return statusRcmd
  } else if (device?.deviceState === missing) {
    return statusMissing
  } else if (device?.deviceState === lost) {
    return statusLost
  } else if (device?.deviceState === decommission) {
    return statusDcmd
  }
  return ""
}

export const getDeviceType = (dType) => {
  const { missing, lost, decommission, recommission } = DEVICE_STATUS;
  const { typeMissing, typeLost, typeDcmd, typeRcmd } = DEVICE_TYPE_VALUE;
  let type
  switch (dType) {
    case typeMissing:
      type = [missing]
      break
    case typeLost:
      type = [lost]
      break
    case typeDcmd:
      type = [decommission]
      break
    case typeRcmd:
      type = [recommission]
      break
    default:
      type = [missing, lost, decommission, recommission]
      break
  }
  return type
}

// Rest of the helper functions remain the same
const executeQuery = async (query) => {
  const snapshot = await query.get();
  return {
    docs: snapshot.docs,
    devices: snapshot.docs.map(doc => ({
      __name__: doc.id,
      deviceStatusText: getDeviceStatus(doc.data()),
      ...doc.data()
    })),
    cursors: {
      first: snapshot.docs[0] || null,
      last: snapshot.docs[snapshot.docs.length - 1] || null
    }
  };
};

// Apply pagination to query
const applyPagination = (query, { startAfterDoc, endBeforeDoc, limit }) => {
  let paginatedQuery = query;
  if (startAfterDoc) {
    paginatedQuery = paginatedQuery.startAfter(startAfterDoc);
  } else if (endBeforeDoc) {
    paginatedQuery = paginatedQuery.endBefore(endBeforeDoc);
  }

  if (limit) {
    paginatedQuery = endBeforeDoc ?
      paginatedQuery.limitToLast(limit) :
      paginatedQuery.limit(limit);
  }
  return paginatedQuery;
};

// Create query with search and filters
const createQuery = ({ context, deviceType, searchText, searchField, orderBy, orderDirection }) => {
  const { companyID, isManager, siteName } = context.currentUser.claims;
  let query = firestore
    .collection('tenants')
    .doc(companyID)
    .collection('Devices')
    .where('deviceState', 'in', getDeviceType(deviceType));
  if (isManager && siteName) {
    query = query.where('siteName', '==', siteName);
  }
  if (searchText && searchField) {
    const searchTerm = searchText.trim();
    const endTerm = searchTerm + '\uf8ff';

    query = query
      .where(searchField, '>=', searchTerm)
      .where(searchField, '<=', endTerm)
      .orderBy(searchField, orderDirection);
  } else {
    query = query.orderBy(orderBy, orderDirection);
  }
  return query;
};

// Main function to fetch devices with pagination
export const fetchDevices = async ({
  limit,
  deviceType,
  startAfterDoc = null,
  endBeforeDoc = null,
  orderBy = DEFAULT_ORDER_BY,
  orderDirection = DEFAUTL_ORDER_DIRECTION,
  searchText = null,
  searchField = null,
  context,
  totalCount = null
}) => {
  console.log(`Fetching devices with params:`, {
    limit,
    deviceType,
    orderBy,
    orderDirection,
    searchText,
    searchField,
    totalCount,
    hasStartAfter: !!startAfterDoc,
    hasEndBefore: !!endBeforeDoc
  });

  try {
    const query = createQuery({
      context,
      deviceType,
      searchText,
      searchField,
      orderBy: searchText ? searchField : orderBy,
      orderDirection
    });

    const paginatedQuery = applyPagination(query, {
      startAfterDoc,
      endBeforeDoc,
      limit
    });

    const { devices, cursors } = await executeQuery(paginatedQuery);

    const count = totalCount ?? await getTotalCount(context, { searchText, searchField, orderDirection, deviceType, orderBy });

    return {
      devices,
      totalCount: count,
      cursors
    };
  } catch (error) {
    console.error('Error fetching devices:', error);
    throw error;
  }
};

export const fetchLastPage = async ({
  context,
  deviceType,
  limit,
  searchText,
  searchField,
  orderBy = DEFAULT_ORDER_BY,
  orderDirection = DEFAUTL_ORDER_DIRECTION,
  startAfterDoc,
  totalCount = null
}) => {

  try {
    const count = totalCount ?? await getTotalCount(context, { searchText, searchField, orderDirection, deviceType });
    const totalPages = Math.floor(count / limit);
    const remainingRecords = count - (totalPages * limit);
    const lastPageLimit = remainingRecords > 0 ? remainingRecords : limit;
    const query = createQuery({
      context,
      deviceType,
      searchText,
      searchField,
      orderBy: searchText ? searchField : orderBy,
      orderDirection
    });
    const snapshot = await query.limitToLast(lastPageLimit).get();
    return {
      users: snapshot.docs.map(doc => ({
        __name__: doc.id,
        deviceStatusText: getDeviceStatus(doc.data()),
        ...doc.data()
      })),
      cursors: {
        first: snapshot.docs[0] || null,
        last: snapshot.docs[snapshot.docs.length - 1] || null
      },
      totalCount: count
    };
  } catch (error) {
    console.error('Error fetching last page:', error);
    throw error;
  }
};

export default {
  ROLES,
  fetchLastPage,
  getTotalCount,
  getDeviceType,
};